/**
 * Model Pricing Configuration Page
 * Admin page for managing LLM model pricing configuration
 * Route: /configuration/model-pricing
 */
import { useState } from 'react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { usePermissions } from '@/hooks/usePermissions'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, MoreVertical, Pencil, Trash2, DollarSign, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import {
  useModelPricing,
  useCreateModelPricing,
  useUpdateModelPricing,
  useDeleteModelPricing
} from '@/api/configuration'
import { PROVIDER_OPTIONS, type ModelPricing, type Provider, type TokenThresholds } from '@/types/pricing'
import { cn } from '@/lib/utils'

/**
 * Format price for display
 */
function formatPrice(price: number | undefined): string {
  if (price === undefined || price === null) {
    return '$0.00'
  }
  return `$${price.toFixed(2)}`
}

/**
 * Get provider badge variant based on provider name
 */
function getProviderBadgeClass(provider: string): string {
  const classes: Record<string, string> = {
    anthropic: 'bg-primary/10 text-primary hover:bg-primary/15',
    openai: 'bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15',
    azure: 'bg-violet-500/10 text-violet-700 hover:bg-violet-500/15',
    google: 'bg-orange-500/10 text-orange-700 hover:bg-orange-500/15',
    other: 'bg-gray-500/10 text-gray-700 hover:bg-gray-500/15'
  }
  return classes[provider] || classes.other
}

/**
 * Get provider display name
 */
function getProviderDisplayName(provider: string): string {
  const option = PROVIDER_OPTIONS.find(p => p.value === provider)
  return option?.label || provider
}

/**
 * Loading skeleton for the table
 */
function ModelPricingSkeleton() {
  return (
    <div data-testid="model-pricing-skeleton" className="space-y-3">
      {[1, 2, 3].map(i => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  )
}

interface ModelPricingFormData {
  modelName: string
  displayName: string
  provider: Provider
  inputPricePer1M: string
  outputPricePer1M: string
  cacheReadPricePer1M: string
  cacheWritePricePer1M: string
  // Session thresholds
  sessionInputWarning: string
  sessionInputCritical: string
  sessionOutputWarning: string
  sessionOutputCritical: string
  // Overall thresholds
  overallInputWarning: string
  overallInputCritical: string
  overallOutputWarning: string
  overallOutputCritical: string
}

const initialFormData: ModelPricingFormData = {
  modelName: '',
  displayName: '',
  provider: 'anthropic',
  inputPricePer1M: '',
  outputPricePer1M: '',
  cacheReadPricePer1M: '',
  cacheWritePricePer1M: '',
  sessionInputWarning: '',
  sessionInputCritical: '',
  sessionOutputWarning: '',
  sessionOutputCritical: '',
  overallInputWarning: '',
  overallInputCritical: '',
  overallOutputWarning: '',
  overallOutputCritical: ''
}

/**
 * Convert form data threshold strings to TokenThresholds object
 */
function formDataToThresholds(formData: ModelPricingFormData): TokenThresholds | null {
  const session = {
    inputTokensWarning: formData.sessionInputWarning ? parseInt(formData.sessionInputWarning) : null,
    inputTokensCritical: formData.sessionInputCritical ? parseInt(formData.sessionInputCritical) : null,
    outputTokensWarning: formData.sessionOutputWarning ? parseInt(formData.sessionOutputWarning) : null,
    outputTokensCritical: formData.sessionOutputCritical ? parseInt(formData.sessionOutputCritical) : null,
  }
  const overall = {
    inputTokensWarning: formData.overallInputWarning ? parseInt(formData.overallInputWarning) : null,
    inputTokensCritical: formData.overallInputCritical ? parseInt(formData.overallInputCritical) : null,
    outputTokensWarning: formData.overallOutputWarning ? parseInt(formData.overallOutputWarning) : null,
    outputTokensCritical: formData.overallOutputCritical ? parseInt(formData.overallOutputCritical) : null,
  }

  // Only include if at least one threshold is set
  const hasSession = Object.values(session).some(v => v !== null)
  const hasOverall = Object.values(overall).some(v => v !== null)

  if (!hasSession && !hasOverall) return null

  return {
    session: hasSession ? session : null,
    overall: hasOverall ? overall : null
  }
}

/**
 * Convert TokenThresholds to form data strings
 */
function thresholdsToFormData(thresholds: TokenThresholds | null | undefined): Partial<ModelPricingFormData> {
  return {
    sessionInputWarning: thresholds?.session?.inputTokensWarning?.toString() ?? '',
    sessionInputCritical: thresholds?.session?.inputTokensCritical?.toString() ?? '',
    sessionOutputWarning: thresholds?.session?.outputTokensWarning?.toString() ?? '',
    sessionOutputCritical: thresholds?.session?.outputTokensCritical?.toString() ?? '',
    overallInputWarning: thresholds?.overall?.inputTokensWarning?.toString() ?? '',
    overallInputCritical: thresholds?.overall?.inputTokensCritical?.toString() ?? '',
    overallOutputWarning: thresholds?.overall?.outputTokensWarning?.toString() ?? '',
    overallOutputCritical: thresholds?.overall?.outputTokensCritical?.toString() ?? '',
  }
}

/**
 * Model Pricing Page Component
 */
export function ModelPricingPage() {
  // State for dialogs
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<ModelPricing | null>(null)
  const [deletingModel, setDeletingModel] = useState<ModelPricing | null>(null)
  const [formData, setFormData] = useState<ModelPricingFormData>(initialFormData)
  const [isThresholdsOpen, setIsThresholdsOpen] = useState(false)

  // API hooks
  const { data, isLoading, error } = useModelPricing()
  const createMutation = useCreateModelPricing()
  const updateMutation = useUpdateModelPricing()
  const deleteMutation = useDeleteModelPricing()

  const pricingList = data?.items ?? []

  // Open add dialog
  const handleAddClick = () => {
    setFormData(initialFormData)
    setIsThresholdsOpen(false)
    setIsAddDialogOpen(true)
  }

  // Open edit dialog
  const handleEditClick = (model: ModelPricing) => {
    const thresholds = thresholdsToFormData(model.thresholds)
    setFormData({
      modelName: model.modelName ?? '',
      displayName: model.displayName ?? '',
      provider: model.provider ?? 'anthropic',
      inputPricePer1M: (model.inputPricePer1M ?? 0).toString(),
      outputPricePer1M: (model.outputPricePer1M ?? 0).toString(),
      cacheReadPricePer1M: model.cacheReadPricePer1M?.toString() ?? '',
      cacheWritePricePer1M: model.cacheWritePricePer1M?.toString() ?? '',
      sessionInputWarning: thresholds.sessionInputWarning ?? '',
      sessionInputCritical: thresholds.sessionInputCritical ?? '',
      sessionOutputWarning: thresholds.sessionOutputWarning ?? '',
      sessionOutputCritical: thresholds.sessionOutputCritical ?? '',
      overallInputWarning: thresholds.overallInputWarning ?? '',
      overallInputCritical: thresholds.overallInputCritical ?? '',
      overallOutputWarning: thresholds.overallOutputWarning ?? '',
      overallOutputCritical: thresholds.overallOutputCritical ?? '',
    })
    // Auto-expand thresholds section if model has thresholds
    setIsThresholdsOpen(model.thresholds != null)
    setEditingModel(model)
  }

  // Open delete confirmation
  const handleDeleteClick = (model: ModelPricing) => {
    setDeletingModel(model)
  }

  // Handle form submission for create
  const handleCreate = async () => {
    await createMutation.mutateAsync({
      modelName: formData.modelName,
      displayName: formData.displayName,
      provider: formData.provider,
      inputPricePer1M: parseFloat(formData.inputPricePer1M),
      outputPricePer1M: parseFloat(formData.outputPricePer1M),
      cacheReadPricePer1M: formData.cacheReadPricePer1M ? parseFloat(formData.cacheReadPricePer1M) : null,
      cacheWritePricePer1M: formData.cacheWritePricePer1M ? parseFloat(formData.cacheWritePricePer1M) : null,
      thresholds: formDataToThresholds(formData)
    })
    setIsAddDialogOpen(false)
    setFormData(initialFormData)
    setIsThresholdsOpen(false)
  }

  // Handle form submission for update
  const handleUpdate = async () => {
    if (!editingModel) return

    await updateMutation.mutateAsync({
      modelName: editingModel.modelName,
      updates: {
        modelName: editingModel.modelName,
        displayName: formData.displayName,
        provider: formData.provider,
        inputPricePer1M: parseFloat(formData.inputPricePer1M),
        outputPricePer1M: parseFloat(formData.outputPricePer1M),
        cacheReadPricePer1M: formData.cacheReadPricePer1M ? parseFloat(formData.cacheReadPricePer1M) : null,
        cacheWritePricePer1M: formData.cacheWritePricePer1M ? parseFloat(formData.cacheWritePricePer1M) : null,
        thresholds: formDataToThresholds(formData)
      }
    })
    setEditingModel(null)
    setFormData(initialFormData)
    setIsThresholdsOpen(false)
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingModel) return

    await deleteMutation.mutateAsync(deletingModel.modelName)
    setDeletingModel(null)
  }

  // Close dialogs
  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false)
    setFormData(initialFormData)
  }

  const handleCloseEditDialog = () => {
    setEditingModel(null)
    setFormData(initialFormData)
  }

  const handleCloseDeleteDialog = () => {
    setDeletingModel(null)
  }

  // Form input handler
  const handleInputChange = (field: keyof ModelPricingFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const isFormValid = 
    (formData.modelName ?? '').trim() !== '' &&
    (formData.displayName ?? '').trim() !== '' &&
    formData.inputPricePer1M !== '' &&
    formData.outputPricePer1M !== '' &&
    parseFloat(formData.inputPricePer1M) >= 0 &&
    parseFloat(formData.outputPricePer1M) >= 0

  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  // Permission check for editing configuration
  const { hasPermission } = usePermissions()
  const canEditConfig = hasPermission('config:edit')

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header */}
        <PageHeader
          title="Model Pricing"
          description="Configure pricing per LLM model for cost estimation"
          actions={
            canEditConfig ? (
              <Button onClick={handleAddClick}>
                <Plus className="h-4 w-4 mr-2" />
                Add Model
              </Button>
            ) : undefined
          }
        />

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load model pricing. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {/* Content */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <ModelPricingSkeleton />
            ) : pricingList.length === 0 ? (
              <EmptyState
                icon={DollarSign}
                title="No pricing configured"
                description="Add model pricing to enable cost estimation"
                action={
                  canEditConfig ? (
                    <Button onClick={handleAddClick}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Model
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model Name</TableHead>
                    <TableHead>Display Name</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead className="text-right">Input $/1M</TableHead>
                    <TableHead className="text-right">Output $/1M</TableHead>
                    <TableHead className="text-right">Cache Read $/1M</TableHead>
                    <TableHead className="text-right">Cache Write $/1M</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingList.map(model => (
                    <TableRow key={model.modelName}>
                      <TableCell className="font-mono text-sm">
                        {model.modelName}
                      </TableCell>
                      <TableCell>{model.displayName}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary" 
                          className={cn(getProviderBadgeClass(model.provider))}
                        >
                          {getProviderDisplayName(model.provider)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(model.inputPricePer1M)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(model.outputPricePer1M)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {model.cacheReadPricePer1M != null ? formatPrice(model.cacheReadPricePer1M) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {model.cacheWritePricePer1M != null ? formatPrice(model.cacheWritePricePer1M) : '—'}
                      </TableCell>
                      <TableCell>
                        {canEditConfig && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label="Actions"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditClick(model)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(model)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Model Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={handleCloseAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Model Pricing</DialogTitle>
              <DialogDescription>
                Configure pricing for a new LLM model.
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="modelName">Model Name *</Label>
                <Input
                  id="modelName"
                  value={formData.modelName}
                  onChange={e => handleInputChange('modelName', e.target.value)}
                  placeholder="claude-sonnet-4-20250514"
                />
                <p className="text-xs text-muted-foreground">
                  Exact model name as returned by the API
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="displayName">Display Name *</Label>
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={e => handleInputChange('displayName', e.target.value)}
                  placeholder="Claude Sonnet 4"
                />
                <p className="text-xs text-muted-foreground">
                  Human-readable name for display
                </p>
              </div>

              <div className="grid gap-2">
                <Label>Provider *</Label>
                <Select
                  value={formData.provider}
                  onValueChange={value => handleInputChange('provider', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="inputPrice">Input Price ($/1M) *</Label>
                  <Input
                    id="inputPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.inputPricePer1M}
                    onChange={e => handleInputChange('inputPricePer1M', e.target.value)}
                    placeholder="3.00"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="outputPrice">Output Price ($/1M) *</Label>
                  <Input
                    id="outputPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.outputPricePer1M}
                    onChange={e => handleInputChange('outputPricePer1M', e.target.value)}
                    placeholder="15.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cacheReadPrice">Cache Read Price ($/1M)</Label>
                  <Input
                    id="cacheReadPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cacheReadPricePer1M}
                    onChange={e => handleInputChange('cacheReadPricePer1M', e.target.value)}
                    placeholder="1.50"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. Uses input price if not set.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cacheWritePrice">Cache Write Price ($/1M)</Label>
                  <Input
                    id="cacheWritePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cacheWritePricePer1M}
                    onChange={e => handleInputChange('cacheWritePricePer1M', e.target.value)}
                    placeholder="15.00"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. Uses output price if not set.
                  </p>
                </div>
              </div>

              {/* Token Thresholds Section */}
              <Collapsible open={isThresholdsOpen} onOpenChange={setIsThresholdsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent">
                    <span className="text-sm font-medium">Token Thresholds (optional)</span>
                    {isThresholdsOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-2">
                  {/* Per Session Thresholds */}
                  <div className="space-y-3 rounded-lg border p-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Per Session</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1">
                        <Label htmlFor="sessionInputWarning" className="text-xs">Input Warning</Label>
                        <Input
                          id="sessionInputWarning"
                          type="number"
                          min="0"
                          value={formData.sessionInputWarning}
                          onChange={e => handleInputChange('sessionInputWarning', e.target.value)}
                          placeholder="50000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="sessionInputCritical" className="text-xs">Input Critical</Label>
                        <Input
                          id="sessionInputCritical"
                          type="number"
                          min="0"
                          value={formData.sessionInputCritical}
                          onChange={e => handleInputChange('sessionInputCritical', e.target.value)}
                          placeholder="200000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="sessionOutputWarning" className="text-xs">Output Warning</Label>
                        <Input
                          id="sessionOutputWarning"
                          type="number"
                          min="0"
                          value={formData.sessionOutputWarning}
                          onChange={e => handleInputChange('sessionOutputWarning', e.target.value)}
                          placeholder="20000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="sessionOutputCritical" className="text-xs">Output Critical</Label>
                        <Input
                          id="sessionOutputCritical"
                          type="number"
                          min="0"
                          value={formData.sessionOutputCritical}
                          onChange={e => handleInputChange('sessionOutputCritical', e.target.value)}
                          placeholder="100000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Overall (Cumulative) Thresholds */}
                  <div className="space-y-3 rounded-lg border p-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Overall (Cumulative)</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1">
                        <Label htmlFor="overallInputWarning" className="text-xs">Input Warning</Label>
                        <Input
                          id="overallInputWarning"
                          type="number"
                          min="0"
                          value={formData.overallInputWarning}
                          onChange={e => handleInputChange('overallInputWarning', e.target.value)}
                          placeholder="1000000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="overallInputCritical" className="text-xs">Input Critical</Label>
                        <Input
                          id="overallInputCritical"
                          type="number"
                          min="0"
                          value={formData.overallInputCritical}
                          onChange={e => handleInputChange('overallInputCritical', e.target.value)}
                          placeholder="5000000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="overallOutputWarning" className="text-xs">Output Warning</Label>
                        <Input
                          id="overallOutputWarning"
                          type="number"
                          min="0"
                          value={formData.overallOutputWarning}
                          onChange={e => handleInputChange('overallOutputWarning', e.target.value)}
                          placeholder="500000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="overallOutputCritical" className="text-xs">Output Critical</Label>
                        <Input
                          id="overallOutputCritical"
                          type="number"
                          min="0"
                          value={formData.overallOutputCritical}
                          onChange={e => handleInputChange('overallOutputCritical', e.target.value)}
                          placeholder="2000000"
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>              </div>            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseAddDialog} disabled={isMutating}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!isFormValid || isMutating}>
                {createMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Model Dialog */}
        <Dialog open={editingModel !== null} onOpenChange={handleCloseEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Model Pricing</DialogTitle>
              <DialogDescription>
                Update pricing configuration for this model.
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="editModelName">Model Name *</Label>
                <Input
                  id="editModelName"
                  value={formData.modelName}
                  disabled
                />
                <p className="text-xs text-muted-foreground">
                  Model name cannot be changed
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="editDisplayName">Display Name *</Label>
                <Input
                  id="editDisplayName"
                  value={formData.displayName}
                  onChange={e => handleInputChange('displayName', e.target.value)}
                  placeholder="Claude Sonnet 4"
                />
              </div>

              <div className="grid gap-2">
                <Label>Provider *</Label>
                <Select
                  value={formData.provider}
                  onValueChange={value => handleInputChange('provider', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="editInputPrice">Input Price ($/1M) *</Label>
                  <Input
                    id="editInputPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.inputPricePer1M}
                    onChange={e => handleInputChange('inputPricePer1M', e.target.value)}
                    placeholder="3.00"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="editOutputPrice">Output Price ($/1M) *</Label>
                  <Input
                    id="editOutputPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.outputPricePer1M}
                    onChange={e => handleInputChange('outputPricePer1M', e.target.value)}
                    placeholder="15.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="editCacheReadPrice">Cache Read Price ($/1M)</Label>
                  <Input
                    id="editCacheReadPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cacheReadPricePer1M}
                    onChange={e => handleInputChange('cacheReadPricePer1M', e.target.value)}
                    placeholder="1.50"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. Uses input price if not set.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="editCacheWritePrice">Cache Write Price ($/1M)</Label>
                  <Input
                    id="editCacheWritePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cacheWritePricePer1M}
                    onChange={e => handleInputChange('cacheWritePricePer1M', e.target.value)}
                    placeholder="15.00"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. Uses output price if not set.
                  </p>
                </div>
              </div>

              {/* Token Thresholds Section */}
              <Collapsible open={isThresholdsOpen} onOpenChange={setIsThresholdsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent">
                    <span className="text-sm font-medium">Token Thresholds (optional)</span>
                    {isThresholdsOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-2">
                  {/* Per Session Thresholds */}
                  <div className="space-y-3 rounded-lg border p-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Per Session</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1">
                        <Label htmlFor="editSessionInputWarning" className="text-xs">Input Warning</Label>
                        <Input
                          id="editSessionInputWarning"
                          type="number"
                          min="0"
                          value={formData.sessionInputWarning}
                          onChange={e => handleInputChange('sessionInputWarning', e.target.value)}
                          placeholder="50000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="editSessionInputCritical" className="text-xs">Input Critical</Label>
                        <Input
                          id="editSessionInputCritical"
                          type="number"
                          min="0"
                          value={formData.sessionInputCritical}
                          onChange={e => handleInputChange('sessionInputCritical', e.target.value)}
                          placeholder="200000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="editSessionOutputWarning" className="text-xs">Output Warning</Label>
                        <Input
                          id="editSessionOutputWarning"
                          type="number"
                          min="0"
                          value={formData.sessionOutputWarning}
                          onChange={e => handleInputChange('sessionOutputWarning', e.target.value)}
                          placeholder="20000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="editSessionOutputCritical" className="text-xs">Output Critical</Label>
                        <Input
                          id="editSessionOutputCritical"
                          type="number"
                          min="0"
                          value={formData.sessionOutputCritical}
                          onChange={e => handleInputChange('sessionOutputCritical', e.target.value)}
                          placeholder="100000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Overall (Cumulative) Thresholds */}
                  <div className="space-y-3 rounded-lg border p-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Overall (Cumulative)</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1">
                        <Label htmlFor="editOverallInputWarning" className="text-xs">Input Warning</Label>
                        <Input
                          id="editOverallInputWarning"
                          type="number"
                          min="0"
                          value={formData.overallInputWarning}
                          onChange={e => handleInputChange('overallInputWarning', e.target.value)}
                          placeholder="1000000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="editOverallInputCritical" className="text-xs">Input Critical</Label>
                        <Input
                          id="editOverallInputCritical"
                          type="number"
                          min="0"
                          value={formData.overallInputCritical}
                          onChange={e => handleInputChange('overallInputCritical', e.target.value)}
                          placeholder="5000000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="editOverallOutputWarning" className="text-xs">Output Warning</Label>
                        <Input
                          id="editOverallOutputWarning"
                          type="number"
                          min="0"
                          value={formData.overallOutputWarning}
                          onChange={e => handleInputChange('overallOutputWarning', e.target.value)}
                          placeholder="500000"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label htmlFor="editOverallOutputCritical" className="text-xs">Output Critical</Label>
                        <Input
                          id="editOverallOutputCritical"
                          type="number"
                          min="0"
                          value={formData.overallOutputCritical}
                          onChange={e => handleInputChange('overallOutputCritical', e.target.value)}
                          placeholder="2000000"
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseEditDialog} disabled={isMutating}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={!isFormValid || isMutating}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deletingModel !== null} onOpenChange={handleCloseDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Model Pricing</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete pricing for:
                <br />
                <strong className="font-mono">{deletingModel?.modelName}</strong>
                <br /><br />
                This will affect cost calculations for sessions using this model.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isMutating}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PageShell>
  )
}
